import { useCallback, useId, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, dialogPanelClass, ModalBackdrop } from '../../components/ui'
import { useFocusTrap } from '../../lib/useFocusTrap'
import { confirmShape, type ConfirmMode } from './confirmMode'

interface Ask {
  mode: ConfirmMode
  text: string
  detail?: string
  okLabel?: string
}

/** Rendered as ordinary DOM, so the host cannot swallow it. `confirmDelete` takes a title naming the record. */
export function useConfirm(): {
  confirm: (text: string, okLabel?: string) => Promise<boolean>
  confirmDelete: (title: string, options?: { detail?: string; okLabel?: string }) => Promise<boolean>
  confirmDeleteNamed: (name: string, options?: { detail?: string; okLabel?: string }) => Promise<boolean>
  dialog: ReactNode
} {
  const { t } = useTranslation()
  const [ask, setAsk] = useState<Ask | null>(null)
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setAsk(null)
  }, [])

  const open = useCallback((next: Ask) => {
    // A second ask while one is open cancels the first: never two pending.
    resolver.current?.(false)
    setAsk(next)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  const confirm = useCallback(
    (text: string, okLabel?: string) => open({ mode: 'ask', text, okLabel }),
    [open],
  )

  const confirmDelete = useCallback(
    (title: string, options?: { detail?: string; okLabel?: string }) =>
      open({ mode: 'delete', text: title, detail: options?.detail, okLabel: options?.okLabel }),
    [open],
  )

  const confirmDeleteNamed = useCallback(
    (name: string, options?: { detail?: string; okLabel?: string }) =>
      confirmDelete(name.trim() ? t('action.deleteNamed', { name }) : t('action.confirmDelete'), options),
    [confirmDelete, t],
  )

  const dialog = ask === null ? null : <ConfirmPanel ask={ask} onSettle={settle} />
  return { confirm, confirmDelete, confirmDeleteNamed, dialog }
}

function ConfirmPanel({ ask, onSettle }: { ask: Ask; onSettle: (ok: boolean) => void }) {
  const { t } = useTranslation()
  const trapRef = useFocusTrap<HTMLDivElement>()
  const textId = useId()
  const shape = confirmShape(ask.mode)
  const isDelete = ask.mode === 'delete'
  // Read while rendering: `autoFocus` has moved focus by the time the trap's effect looks, so it would hand focus back to this closing panel.
  const [opener] = useState(() => document.activeElement as HTMLElement | null)
  const settle = (ok: boolean) => {
    onSettle(ok)
    opener?.focus()
  }
  return (
    // Stops Escape here so a confirm opened from an editor does not close the editor too.
    <div onKeyDown={(event) => event.key === 'Escape' && event.stopPropagation()}>
    <ModalBackdrop
      ref={trapRef}
      role="alertdialog"
      aria-labelledby={textId}
      dismissOnBackdrop={false}
      onClose={() => settle(false)}
    >
      <div className={`${dialogPanelClass} text-left`}>
        {isDelete ? (
          <h2 id={textId} className="text-base font-semibold text-slate-900 dark:text-slate-100">{ask.text}</h2>
        ) : (
          <p id={textId} className="whitespace-pre-line text-sm text-slate-700 dark:text-slate-200">{ask.text}</p>
        )}
        {ask.detail && <p className="whitespace-pre-line text-sm text-slate-700 dark:text-slate-200">{ask.detail}</p>}
        <div className="flex justify-end gap-2">
          <Button autoFocus={shape.focus === 'cancel'} onClick={() => settle(false)}>{t('action.cancel')}</Button>
          <Button variant={shape.okVariant} autoFocus={shape.focus === 'ok'} onClick={() => settle(true)}>
            {ask.okLabel ?? (isDelete ? t('action.delete') : t('roster.confirmOk'))}
          </Button>
        </div>
      </div>
    </ModalBackdrop>
    </div>
  )
}
